import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
import { startHeadGuardian } from "./lib/headGuardian";
import { startFaqScrubber } from "./lib/faqScrubber";
import { startBuildFreshnessCheck } from "./lib/buildFreshness";

startHeadGuardian();
startFaqScrubber();
startBuildFreshnessCheck();

// Auto-recover from stale lazy-chunk errors after a redeploy.
// When index.html references an asset hash that no longer exists,
// dynamic import() throws "Failed to fetch dynamically imported module".
// One-shot reload (guarded by sessionStorage) clears the cache.
const CHUNK_RELOAD_KEY = "__chunk_reload_attempted_at__";
// Allow a fresh recovery attempt after this window, so a later redeploy
// isn't permanently stuck on a blank screen from an earlier attempt.
const CHUNK_RELOAD_COOLDOWN_MS = 30_000;
function isChunkLoadError(msg: string | undefined) {
  if (!msg) return false;
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("error loading dynamically imported module")
  );
}
function handleChunkFailure() {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    if (last && Date.now() - last < CHUNK_RELOAD_COOLDOWN_MS) return;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // storage unavailable — still attempt one reload
  }
  // Cache-bust so the browser re-fetches index.html and its new asset hashes.
  const url = new URL(window.location.href);
  url.searchParams.set("__r", String(Date.now()));
  window.location.replace(url.toString());
}
window.addEventListener("error", (e) => {
  if (isChunkLoadError(e?.message)) handleChunkFailure();
});
window.addEventListener("unhandledrejection", (e) => {
  const msg = (e?.reason && (e.reason.message || String(e.reason))) || "";
  if (isChunkLoadError(msg)) handleChunkFailure();
});

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <App />
  </HelmetProvider>
);
